import { useMemo } from 'react'

import type { CurrentUser } from 'api/src/lib/authTypes'
import { match } from 'ts-pattern'
import { Role } from 'types/graphql'

import { createDbAuthClient, createAuth } from '@cedarjs/auth-dbauth-web'

import { appType } from './utils/appType'

const emrDbAuthClient = createDbAuthClient({
  fetchConfig: { credentials: 'include' },
})

const EmrAuth = createAuth(emrDbAuthClient)

const patientPortalAuthClient = createDbAuthClient({
  dbAuthUrl: `${process.env.REDWOOD_API_URL}/patientPortalAuth`,
  fetchConfig: { credentials: 'include' },
})

const PatientPortalAuth = createAuth(patientPortalAuthClient)

const adminAuthClient = createDbAuthClient({
  dbAuthUrl: `${process.env.REDWOOD_API_URL}/adminDbAuth`,
  fetchConfig: { credentials: 'include' },
})
const AdminAuth = createAuth(adminAuthClient)

export const AuthProvider = match(appType)
  .with('emr', () => EmrAuth.AuthProvider)
  .with('patientPortal', () => PatientPortalAuth.AuthProvider)
  .with('admin', () => AdminAuth.AuthProvider)
  .exhaustive()

export const useAuth = match(appType)
  .with('emr', () => EmrAuth.useAuth)
  .with('patientPortal', () => PatientPortalAuth.useAuth)
  .with('admin', () => AdminAuth.useAuth)
  .exhaustive()

export const useEmrAuth = () => {
  const authResult = useAuth()

  const emrAuth = useMemo(() => {
    const currentUser = authResult.currentUser as CurrentUser

    const emrUser =
      currentUser?.application === 'emr' ? currentUser.emrUser : null

    const hasRole = (roleArg: Role | Role[]) => {
      if (!emrUser) return false

      const roles = typeof roleArg === 'string' ? [roleArg] : roleArg

      return roles.every((role) => emrUser.roles.includes(role))
    }

    const getCurrentUser = () =>
      authResult.getCurrentUser().then((user) => {
        const currentUser = user as CurrentUser
        if (currentUser?.application !== 'emr') return null

        return currentUser.emrUser
      })

    return {
      ...authResult,
      hasRole,
      currentUser: emrUser,
      getCurrentUser,
    }
  }, [authResult])

  return emrAuth
}

const patientPortalHasRole = () => false

export const usePatientPortalAuth = () => {
  const authResult = useAuth()

  const patientPortalAuth = useMemo(() => {
    const currentUser = authResult.currentUser as CurrentUser

    const portalUser =
      currentUser?.application === 'patientPortal'
        ? currentUser.portalUser
        : null

    const getCurrentUser = () =>
      authResult.getCurrentUser().then((user) => {
        const currentUser = user as CurrentUser
        if (currentUser?.application !== 'patientPortal') return null

        return currentUser.portalUser
      })

    return {
      ...authResult,
      hasRole: patientPortalHasRole,
      currentUser: portalUser,
      getCurrentUser,
    }
  }, [authResult])

  return patientPortalAuth
}

export const useAdminAuth = () => {
  const authResult = useAuth()

  const adminAuth = useMemo(() => {
    const currentUser = authResult.currentUser as CurrentUser

    const adminUser =
      currentUser?.application === 'admin' ? currentUser.adminUser : null

    const getCurrentUser = () =>
      authResult.getCurrentUser().then((user) => {
        if (user?.application !== 'admin') return null

        return user.adminUser
      })

    return {
      ...authResult,
      hasRole: () => false,
      currentUser: adminUser,
      getCurrentUser,
    }
  }, [authResult])

  return adminAuth
}

// The `logIn` function exposes on `useAuth` doesn't let you pass in extra arguments
// beyond username/password, so need to directly make a request to the auth endpoint
export const logInWithMfa = async ({
  username,
  password,
  mfaCode,
  trustDevice,
}: {
  username: string
  password: string
  mfaCode: string
  trustDevice: boolean
}) => {
  const res = await fetch(`${process.env.REDWOOD_API_URL}/auth`, {
    method: 'POST',
    body: JSON.stringify({
      method: 'login',
      username,
      password,
      mfaCode,
      trustDevice,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })

  return res.json()
}
